<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const auth = useAuthStore()
</script>

<template>
  <div class="landing-shell">
    <div class="landing-topbar">
      <div class="landing-brand">SaleMonster</div>
      <div class="landing-spacer"></div>
      <div class="op-chip" v-if="auth.isAuthenticated">
        <span class="dot"></span> {{ auth.displayName }}
      </div>
      <button class="btn btn-icon topbar-icon-btn" title="ログアウト" @click="auth.logout()">
        <svg class="icon" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
      </button>
    </div>

    <div class="landing-body">
      <div class="landing-heading">
        <div class="landing-title">どちらを開きますか？</div>
        <div class="landing-sub">利用する画面をタイルから選んでください</div>
      </div>

      <div class="landing-tiles">
        <button class="landing-tile landing-tile--edit" @click="router.push('/product-edit')">
          <div class="landing-tile-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </div>
          <div class="landing-tile-title">セルモン商品編集</div>
          <div class="landing-tile-desc">Amazonカタログからの情報取得・商品情報の編集・画像管理を行います</div>
          <div class="landing-tile-arrow">
            開く
            <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </div>
        </button>

        <button class="landing-tile landing-tile--judge" @click="router.push('/judge')">
          <div class="landing-tile-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 11l3 3L22 4"/>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
            </svg>
          </div>
          <div class="landing-tile-title">セルモン商品審査</div>
          <div class="landing-tile-desc">出品商品の審査・NGワード判定・カテゴリ割当を行います</div>
          <div class="landing-tile-arrow">
            開く
            <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </div>
        </button>

        <!-- Adminグループのみ表示。Generalはこのタイル自体が見えない
             (実際のアクセス制御はAPI側のis_adminで行っており、これは
             メニューを隠すためのUXでしかない点に注意) -->
        <button v-if="auth.isAdmin" class="landing-tile landing-tile--users" @click="router.push('/users')">
          <div class="landing-tile-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div class="landing-tile-title">ユーザ管理</div>
          <div class="landing-tile-desc">ログインユーザーの登録・編集・グループ管理を行います</div>
          <div class="landing-tile-arrow">
            開く
            <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.landing-shell{
  min-height:100vh;display:flex;flex-direction:column;background:var(--bg);
}
.landing-topbar{
  display:flex;align-items:center;gap:14px;padding:0 20px;height:58px;flex:none;
  background:var(--panel);border-bottom:1px solid var(--line);box-shadow:var(--shadow-sm);
}
.landing-brand{font-weight:700;font-size:16px;color:var(--brand-dark);}
.landing-spacer{flex:1;}

.landing-body{
  flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
  padding:40px 20px;
}
.landing-heading{text-align:center;margin-bottom:36px;}
.landing-title{font-size:22px;font-weight:700;color:var(--ink);}
.landing-sub{font-size:13px;color:var(--ink-soft);margin-top:6px;}

.landing-tiles{
  display:flex;gap:24px;flex-wrap:wrap;justify-content:center;max-width:760px;
}
.landing-tile{
  width:330px;padding:28px 26px;border-radius:16px;border:1px solid var(--line);
  display:flex;flex-direction:column;align-items:flex-start;gap:10px;text-align:left;
  cursor:pointer;transition:transform .15s ease, box-shadow .15s ease;
  box-shadow:var(--shadow-sm);
}
.landing-tile:hover{transform:translateY(-3px);box-shadow:var(--shadow-md);}

/* 審査Webタイル: ヘッダーと同じパープル/ピンク/イエロー系グラデーション */
.landing-tile--judge{
  background:linear-gradient(115deg,#F1E9FA 0%,#FBEAF2 45%,#FDF1E2 78%,#FFF8E2 100%);
}
.landing-tile--judge .landing-tile-icon{background:linear-gradient(135deg,#9B7BD1,#6D4FA8);}
.landing-tile--judge .landing-tile-title{color:#4A2E7A;}
.landing-tile--judge .landing-tile-arrow{color:#4A2E7A;}

/* 商品編集Webタイル: ヘッダーと同じブルー/ティール/グリーン系グラデーション */
.landing-tile--edit{
  background:linear-gradient(115deg,#E7F3FC 0%,#E3F7F1 45%,#F0FBEE 78%,#EAFBF5 100%);
}
.landing-tile--edit .landing-tile-icon{background:linear-gradient(135deg,#5FB8D9,#0F5A73);}
.landing-tile--edit .landing-tile-title{color:#0F5A73;}
.landing-tile--edit .landing-tile-arrow{color:#0F5A73;}

/* ユーザ管理タイル: アンバー系グラデーション(審査=紫, 編集=ティールと区別) */
.landing-tile--users{
  background:linear-gradient(115deg,#FDF3E3 0%,#FDEFE0 45%,#FCEAE3 78%,#FBEDEE 100%);
}
.landing-tile--users .landing-tile-icon{background:linear-gradient(135deg,#E5A94F,#B0691F);}
.landing-tile--users .landing-tile-title{color:#8A4F16;}
.landing-tile--users .landing-tile-arrow{color:#8A4F16;}

.landing-tile-icon{
  width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;
  box-shadow:inset 0 0 0 1px rgba(255,255,255,.3);
}
.landing-tile-icon svg{width:24px;height:24px;stroke:#fff;stroke-width:1.8;}
.landing-tile-title{font-size:17px;font-weight:700;}
.landing-tile-desc{font-size:12.5px;color:var(--ink-soft);line-height:1.6;}
.landing-tile-arrow{
  margin-top:6px;font-size:12.5px;font-weight:700;display:flex;align-items:center;gap:4px;
}
</style>
